import styled from "styled-components";
import { Link } from "react-router-dom";

import Button from "../../components/Buttons/Button";
import { pxToRem, withAlpha } from "../../styles/utils";

export const TeachersPageShell = styled.div`
  width: 100%;
  max-width: ${pxToRem(900)};
`;

export const TeachersPageTitle = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xl)};
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.02em;
`;

export const TeachersPageLead = styled.p`
  max-width: ${pxToRem(650)};
  margin: ${({ theme }) => pxToRem(theme.spacing.xxs)} 0 ${({ theme }) => pxToRem(theme.spacing.xl)};
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.64)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;
`;

export const TeacherList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
`;

export const TeacherListLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => pxToRem(theme.spacing.lg)};
  width: 100%;
  min-height: ${pxToRem(76)};
  padding: ${({ theme }) => pxToRem(theme.spacing.md)} ${({ theme }) => pxToRem(theme.spacing.lg)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
  color: inherit;
  text-decoration: none;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => withAlpha(theme.colors.brand, 0.45)};
    background: ${({ theme }) => withAlpha(theme.colors.brand, 0.06)};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }

  @media (max-width: 720px) {
    align-items: flex-start;
    gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
    padding: ${({ theme }) => pxToRem(theme.spacing.md)};
  }
`;

export const TeacherListMain = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`;

export const TeacherName = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: -0.01em;
`;

export const TeacherMeta = styled.p`
  margin: ${({ theme }) => pxToRem(theme.spacing.xxs)} 0 0;
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.54)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;
`;

export const TeacherListAside = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.md)};
  flex: 0 0 auto;

  @media (max-width: 720px) {
    gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  }
`;

export const TeacherTelegramStatus = styled.span<{ $connected?: boolean }>`
  color: ${({ theme, $connected }) =>
    $connected ? theme.colors.successText : withAlpha(theme.colors.ink, 0.56)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
  line-height: 1.35;
  white-space: nowrap;

  @media (max-width: 520px) {
    display: none;
  }
`;

export const TeacherChevron = styled.span`
  width: ${pxToRem(10)};
  height: ${pxToRem(10)};
  flex: 0 0 auto;
  border-top: ${pxToRem(2)} solid ${({ theme }) => withAlpha(theme.colors.ink, 0.42)};
  border-right: ${pxToRem(2)} solid ${({ theme }) => withAlpha(theme.colors.ink, 0.42)};
  transform: rotate(45deg);
`;

export const TeacherDetailSurface = styled.section`
  width: 100%;
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
`;

export const TeacherDetailSection = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => pxToRem(theme.spacing.lg)};
  padding: ${({ theme }) => pxToRem(theme.spacing.lg)};

  & + & {
    border-top: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
  }

  @media (max-width: 720px) {
    flex-direction: column;
    gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
    padding: ${({ theme }) => pxToRem(theme.spacing.md)};
  }
`;

export const DetailSectionMain = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`;

export const DetailSectionTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 600;
  line-height: 1.35;
`;

export const DetailSectionText = styled.p`
  max-width: ${pxToRem(560)};
  margin: ${({ theme }) => pxToRem(theme.spacing.xxs)} 0 0;
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.56)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;
`;

export const DetailSectionSide = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  flex: 0 0 auto;

  @media (max-width: 720px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

export const TeacherCatalogBlock = styled.section`
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xl)};
`;

export const TeacherCatalogTitle = styled.h2`
  margin: 0 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 600;
  line-height: 1.35;
`;

export const TelegramButton = styled(Button).attrs({ size: "sm" })`
  width: auto;
  min-width: 0;
  max-width: none;
  font-weight: 500;
`;

export const TelegramLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: transparent;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.inkSurface};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;

export const TeacherCourseList = styled.div`
  width: 100%;
  margin-top: ${({ theme }) => pxToRem(theme.spacing.sm)};
  border-top: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
`;

export const TeacherCourseRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  min-height: ${pxToRem(50)};
  padding: ${({ theme }) => pxToRem(theme.spacing.xs)} 0;

  & + & {
    border-top: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
  }
`;

export const TeacherCourseMain = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`;

export const TeacherCourseTitle = styled.div`
  overflow: hidden;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TeacherCourseMeta = styled.div`
  margin-top: ${pxToRem(2)};
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.44)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.35;
`;

export const CourseOpenLink = styled(Link)`
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.brand};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;

export const CatalogLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: transparent;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.inkSurface};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;

export const EmptyTeachers = styled.div`
  padding: ${({ theme }) => pxToRem(theme.spacing.xxl)};
  border: ${pxToRem(1)} dashed ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  text-align: center;
`;

export const EmptyTeachersTitle = styled.strong`
  display: block;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 500;
`;

export const EmptyTeachersText = styled.span`
  display: block;
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.48)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;
`;

export const TeachersLoading = styled.div`
  padding: ${({ theme }) => pxToRem(theme.spacing.xl)} 0;
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.42)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
`;
